import { getTenantCode, getToken } from "../context/auth/auth-storage";
import {
  NetworkError,
  NotAuthorizedError,
  BadRequestError,
  ConflictError,
  TenantModuleDisabledError,
} from "./network-error";

const moduleDisabledSubscribers = new Set();

export const subscribeToModuleDisabled = (subscriber) => {
  moduleDisabledSubscribers.add(subscriber);
  return () => moduleDisabledSubscribers.delete(subscriber);
};

const publishModuleDisabled = (moduleKey, message) => {
  moduleDisabledSubscribers.forEach((subscriber) =>
    subscriber(moduleKey, message)
  );
};

export const baseURL = process.env.REACT_APP_API_URL;

export const createRequestHeaders = ({
  auth = false,
  tenantBound = auth,
  tenantCode,
  json = true,
  headers = {},
} = {}) => {
  const requestHeaders = {
    "Cache-Control": "no-cache",
  };

  if (json) {
    requestHeaders["Content-Type"] = "application/json";
  }

  if (auth) {
    const token = getToken();
    if (token) {
      requestHeaders["Authorization"] = `Bearer ${token}`;
    }
  }

  if (tenantBound) {
    const resolvedTenantCode = tenantCode ?? getTenantCode();
    if (resolvedTenantCode) {
      requestHeaders["X-Tenant-Code"] = resolvedTenantCode;
    }
  }

  return {
    ...requestHeaders,
    ...headers,
  };
};

const buildUrl = (path) => {
  return `${baseURL}/${path}`;
};

const parseResponseBody = async (response, download = false) => {
  if (download) {
    return response.blob();
  }

  const text = await response.text();
  if (text === "") {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch (e) {
    throw new NetworkError("Unable to parse server response", response.status);
  }
};

const request = async ({
  path,
  method,
  body,
  auth = false,
  tenantBound = auth,
  tenantCode,
  download = false,
  json = true,
  headers,
  signal,
}) => {
  const fetchOptions = {
    method,
    body,
    headers: createRequestHeaders({
      auth,
      tenantBound,
      tenantCode,
      json,
      headers,
    }),
  };

  if (signal) {
    fetchOptions.signal = signal;
  }

  const response = await fetch(buildUrl(path), fetchOptions);

  const result = await parseResponseBody(response, download);

  if (response.status >= 200 && response.status < 300) {
    return result;
  }

  errorResponseParser(result, response.status);
};

export const postRequest = async (
  path,
  requestData,
  auth = false,
  options = {}
) => {
  return request({
    path,
    method: "POST",
    body: JSON.stringify(requestData),
    auth,
    ...options,
  });
};

export const putRequest = async (
  path,
  requestData,
  auth = false,
  options = {}
) => {
  return request({
    path,
    method: "PUT",
    body: JSON.stringify(requestData),
    auth,
    ...options,
  });
};

export const getRequest = async (
  path,
  auth = false,
  download = false,
  options = {}
) => {
  return request({
    path,
    method: "GET",
    auth,
    download,
    ...options,
  });
};

export const getTenantConfig = async () => {
  return request({
    path: "app/config",
    method: "GET",
    auth: true,
    tenantBound: true,
  });
};

export const uploadRequest = async (
  path,
  data,
  isEdit = false,
  options = {}
) => {
  return request({
    path,
    method: isEdit ? "PUT" : "POST",
    body: data,
    auth: true,
    json: false,
    ...options,
  });
};

export const deleteRequest = async (path, id, options = {}) => {
  return request({
    path: `${path}/${id}`,
    method: "DELETE",
    auth: true,
    ...options,
  });
};

export const patchRequest = async (path, requestData, options = {}) => {
  return request({
    path,
    method: "PATCH",
    body: JSON.stringify(requestData),
    auth: true,
    ...options,
  });
};

const errorResponseParser = (response, statusCode) => {
  const message = response?.message;
  if (statusCode === undefined) {
    statusCode = response?.status;
  }
  if (
    statusCode === 403 &&
    response?.code === "MODULE_DISABLED" &&
    response?.module
  ) {
    publishModuleDisabled(response.module, message);
    throw new TenantModuleDisabledError(
      message ?? `Module '${response.module}' is disabled for this tenant.`,
      response.module
    );
  } else if (statusCode === 401 || statusCode === 403) {
    throw new NotAuthorizedError(message ?? "Not authorized", statusCode);
  } else if (statusCode === 400) {
    if (message !== undefined) {
      throw new BadRequestError(message);
    } else {
      const errorMessages = response?.errors;
      throw new NetworkError(errorMessages, statusCode);
    }
  } else if (statusCode === 409) {
    throw new ConflictError(message ?? "The request conflicts with current state.");
  } else {
    throw new NetworkError(
      message ?? "Unable to process your request",
      statusCode
    );
  }
};

export default NotAuthorizedError;
